import { useCallback, useEffect, useRef, useState } from "react";
import { FolderCheck, Upload, FileText, Trash2, Eye, Loader2, Paperclip, ClipboardList } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

export default function DocumentsApplications() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("documents");
  const [uploading, setUploading] = useState(false);
  const [cat, setCat] = useState("Other");
  const [cust, setCust] = useState("");
  const fileRef = useRef(null);
  const load = useCallback(() => { api.get("/documents/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { documents, applications, customers, categories, stats } = data;

  const doUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", cat); if (cust) fd.append("customer_id", cust);
    setUploading(true);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const preview = async (id) => {
    try { const { data } = await api.get(`/documents/${id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
    catch { toast.error("Could not open file."); }
  };
  const assign = async (id, customer_id) => { try { await api.patch(`/documents/${id}`, { customer_id }); load(); } catch { toast.error("Could not assign."); } };
  const remove = async (id) => { try { await api.delete(`/documents/${id}`); load(); } catch { toast.error("Could not delete."); } };

  return (
    <div className="animate-fade-up space-y-6" data-testid="documents-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><FolderCheck size={22} className="text-[#b0862f]" /> Documents & Applications</h1>
        <p className="mt-1 text-[13px] text-slate-500">Upload and organize real documents, assign them to customers, and track application packages your AI team assembles.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Documents" value={stats.documents} tone="slate" />
        <Stat label="Assigned" value={stats.assigned} tone="blue" />
        <Stat label="Applications" value={stats.applications} tone="green" />
        <Stat label="Action required" value={stats.action_required} tone="red" />
      </div>

      <div className="flex gap-5 border-b border-slate-200">
        {[["documents", "Documents", Paperclip], ["applications", "Applications", ClipboardList]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`docs-tab-${id}`}
            className={`flex items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${tab === id ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "documents" && (
        <div className="space-y-4" data-testid="docs-documents">
          <Card className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Category</span>
                <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="upload-category" className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]">
                  {categories.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Assign to (optional)</span>
                <select value={cust} onChange={(e) => setCust(e.target.value)} data-testid="upload-customer" className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]">
                  <option value="">— Unassigned —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <input ref={fileRef} type="file" onChange={doUpload} className="hidden" data-testid="upload-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading…" : "Upload document"}
              </button>
            </div>
          </Card>
          {documents.length === 0 ? (
            <Card className="p-8 text-center text-[13px] text-slate-400">No documents yet. Upload your first above.</Card>
          ) : (
            <div className="space-y-2">
              {documents.map((d) => (
                <Card key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="document-row">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><FileText size={17} /></span>
                    <div className="min-w-0">
                      <div className="truncate font-head text-[14px] font-bold text-slate-900">{d.name}</div>
                      <div className="font-mono text-[10px] text-slate-400">{d.category} · {(d.size / 1024).toFixed(0)} KB · {String(d.created_at).slice(0, 10)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={d.customer_id || ""} onChange={(e) => assign(d.id, e.target.value)} data-testid="doc-assign" className="rounded-lg border border-slate-200 px-2 py-1.5 text-[12px] outline-none focus:border-[#1a5fd0]">
                      <option value="">Unassigned</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <button onClick={() => preview(d.id)} data-testid="doc-preview" className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-200"><Eye size={13} /> Preview</button>
                    <button onClick={() => remove(d.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "applications" && (
        <div className="space-y-2" data-testid="docs-applications">
          {applications.length === 0 ? (
            <Card className="p-8 text-center text-[13px] text-slate-400">No application packages yet. They appear here as your Business Credit AI Employee prepares vendor & funding applications.</Card>
          ) : applications.map((a) => (
            <Card key={a.id} className="flex items-center justify-between gap-3 p-4" data-testid="application-row">
              <div>
                <div className="font-head text-[14px] font-bold text-slate-900">{a.vendor}</div>
                <div className="font-mono text-[10px] text-slate-400">{a.category || "Application"}{a.customer_label ? ` · ${a.customer_label}` : ""} · {String(a.when || "").slice(0, 10)}</div>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider ${/action|missing/i.test(a.status) ? "bg-amber-50 text-amber-600" : /approv|complete|active/i.test(a.status) ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{a.status}</span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
